import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spinner,
  Row,
  Col,
  Container,
  Button,
  Modal,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import Message from "../components/Message";
import {
  desktopDetails,
  desktopDelete,
  desktopUpdate,
  desktopPersist,
  fetchPersistProgress,
} from "../slices/desktopSlice";
import { listTemplates } from "../slices/templateSlice";
import { listImages } from "../slices/imageSlice";
import ModalEditDesktop from "../components/ModalEditDesktop";
import ModalPersistDesktop from "../components/ModalPersistDesktop";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Common.css";
import { FaTrash, FaEdit, FaSave } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { toast } from "react-toastify";

const formatBytes = (bytes) => {
  if (bytes === 0 || !bytes) return "0 B";
  const k = 1000;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

function DesktopDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let params = useParams();

  const {
    error,
    loading,
    desktop,
    flavor: flavorDetails,
  } = useSelector((state) => state.desktop);

  const { templates } = useSelector((state) => state.template || {});
  const { images } = useSelector((state) => state.image || {});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const [showModalEdit, setShowEditModal] = useState(false);
  const [editedDesktop, setEditedDesktop] = useState({});
  const [desktopModalErrors, setDesktopModalErrors] = useState({});
  const [formDataEditDesktop, setformDataEditDesktop] = useState({});

  const handleShowEditModal = () => setShowEditModal(true);
  const handleCloseEditModal = () => {
    setformDataEditDesktop({});
    setShowEditModal(false);
  };

  const [showPersistModal, setShowPersistModal] = useState(false);
  const [formDataPersist, setFormDataPersist] = useState({
    newTemplateName: "",
    diskImagesNames: {},
  });

  const [hoveredFlavor, setHoveredFlavor] = useState(false);
  const [persistProgress, setPersistProgress] = useState([]);

  const extractId = (params_id) => {
    const stateIndex = params_id.indexOf("&state");
    if (stateIndex !== -1) {
      return params_id.slice(0, stateIndex);
    }
    return params_id;
  };

  useEffect(() => {
    const cleanedId = extractId(params.id);
    if (cleanedId) {
      dispatch(desktopDetails(cleanedId));
    }
    dispatch(listTemplates());
    dispatch(listImages());
  }, [dispatch, params.id]);

  useEffect(() => {
    let interval;
    const cleanedId = extractId(params.id);

    const fetchProgress = async () => {
      if (!cleanedId || desktop?.state !== "Persisting") return;
      try {
        const data = await dispatch(fetchPersistProgress(cleanedId)).unwrap();
        if (Array.isArray(data)) {
          setPersistProgress(data);
        }
      } catch (err) {
        // change to silent catch?
        toast.error(
          typeof err === "string"
            ? err
            : err?.detail || "Failed to get persistance updates.",
        );
      }
    };

    if (desktop?.state === "Persisting") {
      fetchProgress();
      interval = setInterval(fetchProgress, 1000);
    }

    return () => clearInterval(interval);
  }, [params.id, desktop?.state, dispatch]);

  useEffect(() => {
    if (desktop && Object.keys(desktop).length > 0) {
      setEditedDesktop((prevDesktop) => {
        if (
          prevDesktop.name !== desktop.name ||
          prevDesktop.description !== desktop.description
        ) {
          return {
            name: desktop.name || "",
            description: desktop.description || "",
          };
        }
        return prevDesktop;
      });

      setformDataEditDesktop((prevFormData) => {
        if (
          prevFormData.name !== desktop.name ||
          prevFormData.description !== desktop.description
        ) {
          return {
            name: desktop.name || "",
            description: desktop.description || "",
          };
        }
        return prevFormData;
      });
    }
  }, [desktop]);

  const handleDelete = () => {
    const cleanedId = extractId(params.id);
    dispatch(desktopDelete(cleanedId));
    navigate("/");
    setShowDeleteModal(false);
  };

  const handleInputChangeEditDesktop = (e) => {
    const { name, value } = e.target;
    setformDataEditDesktop({
      ...formDataEditDesktop,
      [name]: value,
    });
  };

  const handleEditDesktop = (formDataEditDesktop) => {
    const cleanedId = extractId(params.id);
    dispatch(desktopUpdate({ id: cleanedId, desktopData: formDataEditDesktop }))
      .unwrap()
      .then(() => {
        dispatch(desktopDetails(cleanedId));
        handleCloseEditModal();
        toast.success("Desktop updated successfully.");
      })
      .catch((err) => {
        console.error("Error updating desktop: ", err);
        toast.error(
          typeof err === "string"
            ? err
            : err?.detail || "Failed to update desktop.",
        );
      });
  };

  const handleShowPersistModal = () => {
    const templateMatch = templates?.find(
      (t) => String(t.id) === String(desktop.templateId),
    );
    const initialTemplateName = templateMatch
      ? templateMatch.name
      : desktop.templateId || "";

    const initialDiskImages = {};
    if (desktop.imageAttachments && Array.isArray(desktop.imageAttachments)) {
      desktop.imageAttachments.forEach((att) => {
        const imageMatch = images?.find(
          (i) => String(i.id) === String(att.imageId),
        );
        initialDiskImages[att.mountName] = imageMatch
          ? imageMatch.name
          : att.imageId;
      });
    }

    setFormDataPersist({
      newTemplateName: initialTemplateName,
      diskImagesNames: initialDiskImages,
    });
    setShowPersistModal(true);
  };

  const handlePersistDesktop = (persistData) => {
    const cleanedId = extractId(params.id);
    dispatch(desktopPersist({ id: cleanedId, persistData }))
      .unwrap()
      .then(() => {
        setShowPersistModal(false);
        toast.success("Desktop persistence initiated.");
      })
      .catch((err) => {
        console.error("Error persisting desktop: ", err);
        toast.error(
          <div>
            <strong>Failed to Persist</strong>
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              {typeof err === "string"
                ? err
                : err?.detail ||
                  "An error occurred while persisting your desktop."}
            </div>
          </div>,
        );
      });
  };

  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => formatValue(item)).join(", ");
    }

    if (typeof value === "object" && value !== null) {
      if ("key" in value && "value" in value) {
        return `${value.key}: ${formatValue(value.value)}`;
      }

      return Object.entries(value)
        .map(([k, v]) => `${k}: ${formatValue(v)}`)
        .join(", ");
    }

    return String(value ?? "-");
  };

  const hasDesktopLoaded = desktop && Object.keys(desktop).length > 0;

  if (!loading && !hasDesktopLoaded && !error) {
    return (
      <div className="main-container">
        <Header />
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>
            <strong style={{ fontSize: "38px" }}>Desktop not found :(</strong>
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="main-container">
      <Header />

      {/* wrapper to push footer to the bottom */}
      <div style={{ flex: 1, width: "100%", paddingBottom: "30px" }}>
        {loading && !hasDesktopLoaded ? (
          <span style={{ display: "flex", padding: "20px" }}>
            <h5>Getting Desktop Details</h5>
            <span className="ml-2">
              <Spinner animation="border" />
            </span>
          </span>
        ) : error && !hasDesktopLoaded ? (
          <div style={{ padding: "20px" }}>
            <Message variant="danger">{error}</Message>
          </div>
        ) : (
          <div>
            {/* 3-column flex layout */}
            <div
              className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-4 mt-3"
              style={{ padding: "0 20px" }}
            >
              <div className="d-none d-md-block" style={{ flex: 1 }}></div>

              <div className="text-center" style={{ flex: 2 }}>
                <p className="m-0">
                  <strong
                    className="text-capitalize"
                    style={{ fontSize: "clamp(30px, 4vw, 38px)" }}
                  >
                    {desktop?.name || "Desktop Name"}
                  </strong>
                </p>
              </div>

              <div
                className="d-flex justify-content-center justify-content-md-end mt-3 mt-md-0"
                style={{ flex: 1, gap: "5px" }}
              >
                {/* Edit Button */}
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="tooltip-edit" className="custom-tooltip">
                      Edit Desktop
                    </Tooltip>
                  }
                >
                  <Button variant="primary" onClick={handleShowEditModal}>
                    <FaEdit />
                  </Button>
                </OverlayTrigger>

                {/* Persist Button */}
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="tooltip-persist" className="custom-tooltip">
                      Persist Desktop
                    </Tooltip>
                  }
                >
                  <Button
                    variant="success"
                    onClick={handleShowPersistModal}
                    style={{
                      backgroundColor: "#28a745",
                      borderColor: "#28a745",
                    }}
                  >
                    <FaSave />
                  </Button>
                </OverlayTrigger>

                {/* Delete Button */}
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="tooltip-delete" className="custom-tooltip">
                      Delete Desktop
                    </Tooltip>
                  }
                >
                  <Button variant="danger" onClick={handleShowDeleteModal}>
                    <FaTrash />
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            {/* Delete Desktop Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
              <Modal.Header closeButton>
                <Modal.Title>Delete Desktop</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Are you sure you want to delete this desktop?</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseDeleteModal}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Edit Desktop Modal */}
            <ModalEditDesktop
              showModalEditDesktop={showModalEdit}
              handleCloseModalEditDesktop={handleCloseEditModal}
              handleEditDesktop={handleEditDesktop}
              formDataEditDesktop={formDataEditDesktop}
              handleInputChangeEditDesktop={handleInputChangeEditDesktop}
              desktopModalErrors={desktopModalErrors}
            />

            {/* Persist Desktop Modal */}
            <ModalPersistDesktop
              show={showPersistModal}
              handleClose={() => setShowPersistModal(false)}
              handlePersist={handlePersistDesktop}
              formData={formDataPersist}
              setFormData={setFormDataPersist}
              loadingDesktop={loading}
              imageAttachments={desktop?.imageAttachments || []}
            />

            {/* display desktop details */}
            <Container>
              <Row>
                <Col sm>
                  <div style={{ marginBottom: "20px" }}>
                    {hasDesktopLoaded ? (
                      <>
                        {/* display ID */}
                        {desktop.id !== undefined && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              border: "1px solid",
                              borderColor: "#DAE3ED",
                              padding: "5px",
                              margin: "2px 0",
                              wordBreak: "break-word",
                            }}
                          >
                            <span>{"id"}:</span>&nbsp;
                            <span style={{ color: "#4974A5" }}>
                              {desktop.id !== undefined
                                ? String(desktop.id)
                                : "-"}
                            </span>
                          </div>
                        )}

                        {/* display Template ID and info button */}
                        {desktop.templateId !== undefined && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              border: "1px solid",
                              borderColor: "#DAE3ED",
                              padding: "5px",
                              margin: "2px 0",
                              position: "relative", // button placed outside table
                              wordBreak: "break-word",
                            }}
                          >
                            <OverlayTrigger
                              placement="bottom"
                              overlay={
                                <Tooltip
                                  id="tooltip-info"
                                  className="custom-tooltip"
                                >
                                  {desktop.templateId
                                    ? "Info about this template"
                                    : "No template info"}
                                </Tooltip>
                              }
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: "-25px", // distance from info button to templateId
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  cursor: "pointer",
                                }}
                              >
                                <FaCircleInfo
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    color: "#4974A5",
                                  }}
                                  onClick={() => {
                                    if (
                                      desktop.templateId &&
                                      desktop.templateId.trim() !== ""
                                    ) {
                                      navigate(
                                        `/templates/${desktop.templateId}`,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </OverlayTrigger>
                            {/* display Template ID */}
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {/* <a style={{ color: "#9E6482BF", fontWeight: "bold" }}>
                                {"templateId"}:
                              </a> */}
                              {"templateId"}:
                            </span>
                            &nbsp;
                            <span style={{ color: "#4974A5" }}>
                              {desktop.templateId !== undefined
                                ? desktop.templateId
                                : "-"}
                            </span>
                          </div>
                        )}

                        {/* display the other fields */}
                        {Object.keys(desktop).map((key) => {
                          const value = desktop[key];

                          // skip already shown fields (id and templateId)
                          if (key === "id" || key === "templateId") return null;

                          // display flavor field
                          if (key === "flavorId") {
                            const isFlavorEmpty =
                              !flavorDetails ||
                              !value ||
                              value === "null" ||
                              value === "None" ||
                              Object.keys(flavorDetails).length === 0;

                            return (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  justifyContent: "left",
                                  border: "1px solid",
                                  borderColor: "#DAE3ED",
                                  padding: "5px",
                                  margin: "2px 0",
                                  wordBreak: "break-word",
                                }}
                                onMouseEnter={() => setHoveredFlavor(true)}
                                onMouseLeave={() => setHoveredFlavor(false)}
                              >
                                <span>{key}:</span>&nbsp;
                                <span style={{ color: "#4974A5" }}>
                                  {!value ||
                                  ["null", "none"].includes(
                                    value.toLowerCase().trim(),
                                  )
                                    ? "-"
                                    : value}
                                </span>
                                {hoveredFlavor && !isFlavorEmpty && (
                                  <div
                                    style={{
                                      padding: "10px",
                                      zIndex: 100,
                                      top: "20px",
                                      color: "#9E6482BF", // 9E2222BF
                                    }}
                                  >
                                    {Object.entries(flavorDetails || {})
                                      .filter(([key]) => key !== "blob")
                                      .map(([key, value]) => (
                                        <div key={key}>
                                          <strong>{key}:</strong>&nbsp;
                                          {formatValue(value)}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // display the other fields
                          return (
                            <div
                              key={key}
                              style={{
                                display: "flex",
                                justifyContent: "left",
                                border: "1px solid",
                                borderColor: "#DAE3ED",
                                padding: "5px",
                                margin: "2px 0",
                                wordBreak: "break-word",
                              }}
                            >
                              <span>{`${key}:`}</span>&nbsp;
                              <span
                                style={{
                                  color: "#4974A5",
                                  display: "block",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatValue(value).replace(
                                    /\n/g,
                                    "<br />",
                                  ),
                                }}
                              />
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <p>No details available for this desktop.</p>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Persist Progress Bars */}
              {desktop?.state === "Persisting" &&
                persistProgress.length > 0 && (
                  <Row>
                    <Col sm>
                      <div className="persist-progress-container">
                        <h5 className="persist-progress-title">
                          Persisting State...
                        </h5>
                        {persistProgress.map((item, idx) => {
                          const percent =
                            item.totalBytes > 0
                              ? (item.currentBytes / item.totalBytes) * 100
                              : 0;
                          return (
                            <div key={idx} className="persist-progress-item">
                              <div className="persist-progress-header">
                                <strong>{item.name}</strong>
                              </div>
                              <div className="persist-progress-track-container">
                                <div className="persist-progress-track">
                                  <div
                                    className="persist-progress-bar"
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                                <span className="persist-progress-bytes">
                                  {formatBytes(item.currentBytes)} /{" "}
                                  {formatBytes(item.totalBytes)}
                                </span>
                              </div>
                              <div className="persist-progress-speed">
                                {formatBytes(item.bandwidth)}/s
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Col>
                  </Row>
                )}
            </Container>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default DesktopDetailsPage;
